import type {
  PointStyle,
  LineStyle,
  CurveStyle,
  PolygonStyle,
  AngleStyle,
  CircleStyle,
  ElementTheme,
} from '@examdojo/core/widget';
import { deepMerge } from '@examdojo/core/widget';

/**
 * Recursive style node for features.
 * Styles at this node apply to it AND cascade to all descendants.
 * Subparts can override any inherited styles.
 *
 * Labels and snapping are configured within the individual style properties:
 * - point.label, point.snapping
 * - line.label
 * - curve.label
 * - etc.
 */
export interface FeatureStyleNode {
  point?: PointStyle;
  line?: LineStyle;
  curve?: CurveStyle;
  polygon?: PolygonStyle;
  angle?: AngleStyle;
  circle?: CircleStyle;
  visible?: boolean;
  subparts?: Record<string, FeatureStyleNode>;
}

/**
 * Feature theme - maps feature type to its style tree.
 */
export type FeatureTheme = Record<string, FeatureStyleNode>;

/**
 * Style types that can be resolved.
 */
export type StyleType = 'point' | 'line' | 'curve' | 'polygon' | 'angle' | 'circle';

/**
 * Maps style type to its corresponding style interface.
 */
export interface StyleTypeMap {
  point: PointStyle;
  line: LineStyle;
  curve: CurveStyle;
  polygon: PolygonStyle;
  angle: AngleStyle;
  circle: CircleStyle;
}

/**
 * Resolve a style path through a FeatureStyleNode tree.
 * Merges styles from root to leaf, with deeper nodes overriding ancestors.
 */
export function resolveStylePath(node: FeatureStyleNode | undefined, path: string[]): FeatureStyleNode {
  if (!node) {
    return {};
  }

  const result: FeatureStyleNode = {
    point: node.point ? { ...node.point } : undefined,
    line: node.line ? { ...node.line } : undefined,
    curve: node.curve ? { ...node.curve } : undefined,
    polygon: node.polygon ? { ...node.polygon } : undefined,
    angle: node.angle ? { ...node.angle } : undefined,
    circle: node.circle ? { ...node.circle } : undefined,
    visible: node.visible,
  };

  let current = node;
  for (const segment of path) {
    const child = current.subparts?.[segment];
    if (!child) {
      break;
    }

    if (child.point) {
      result.point = deepMerge(result.point, child.point);
    }
    if (child.line) {
      result.line = deepMerge(result.line, child.line);
    }
    if (child.curve) {
      result.curve = deepMerge(result.curve, child.curve);
    }
    if (child.polygon) {
      result.polygon = deepMerge(result.polygon, child.polygon);
    }
    if (child.angle) {
      result.angle = deepMerge(result.angle, child.angle);
    }
    if (child.circle) {
      result.circle = deepMerge(result.circle, child.circle);
    }
    if (child.visible !== undefined) {
      result.visible = child.visible;
    }

    current = child;
  }

  return result;
}

/**
 * Context needed for style resolution (subset of ElementContext to avoid circular deps).
 */
export interface StyleResolutionContext {
  elementFactory: { getTheme: () => ElementTheme };
  featureTheme: FeatureTheme;
}

/**
 * Resolve a style for a feature element.
 *
 * Cascading order:
 * 1. ElementTheme defaults (from factory)
 * 2. FeatureTheme[featureType] (feature-specific defaults)
 * 3. display config (instance overrides)
 */
export function resolveFeatureStyle<T extends StyleType>(
  styleType: T,
  featureType: string,
  context: StyleResolutionContext,
  display: FeatureStyleNode | undefined,
  path: string[] = [],
): StyleTypeMap[T] {
  const { elementFactory, featureTheme } = context;

  // 1. Start with element theme defaults
  let style = { ...elementFactory.getTheme()[styleType] } as StyleTypeMap[T];

  // 2. Apply feature theme (resolved through path)
  const featureNode = featureTheme[featureType];
  const featureResolved = resolveStylePath(featureNode, path);
  if (featureResolved[styleType]) {
    style = deepMerge(style, featureResolved[styleType]) as StyleTypeMap[T];
  }

  // 3. Apply instance display config (resolved through path)
  const displayResolved = resolveStylePath(display, path);
  if (displayResolved[styleType]) {
    style = deepMerge(style, displayResolved[styleType]) as StyleTypeMap[T];
  }

  return style;
}

/**
 * Resolve visibility for a feature element.
 */
export function resolveFeatureVisibility(
  featureType: string,
  featureTheme: FeatureTheme,
  display: FeatureStyleNode | undefined,
  path: string[] = [],
): boolean {
  const featureNode = featureTheme[featureType];
  const featureResolved = resolveStylePath(featureNode, path);
  const displayResolved = resolveStylePath(display, path);

  if (displayResolved.visible !== undefined) {
    return displayResolved.visible;
  }
  if (featureResolved.visible !== undefined) {
    return featureResolved.visible;
  }
  return true;
}
